import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useRef, useState } from 'react';

/** Hodnota pole typu `picture` — tvar držíme stejný, jaký posílalo Tabidoo. */
type Obrazek = { fileUrl: string; thumbnailUrl: string };

/** 3 MB na frontendu; backend má o megabajt rezervu navíc. */
const MAX_BYTES = 3 * 1024 * 1024;

/** Z uložené hodnoty vytáhne URL obrázků (Tabidoo i naše mají `fileUrl`). */
export function asImages(v: unknown): string[] {
  const arr = Array.isArray(v) ? v : v ? [v] : [];
  const out: string[] = [];
  for (const it of arr) {
    if (it && typeof it === 'object') {
      const o = it as Record<string, unknown>;
      const u =
        (typeof o.thumbnailUrl === 'string' && o.thumbnailUrl) ||
        (typeof o.fileUrl === 'string' && o.fileUrl) ||
        '';
      if (u) out.push(u);
    }
  }
  return out;
}

/** Soubor → data: URL, kterou umí přečíst backend. */
function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Soubor se nepodařilo přečíst'));
    reader.readAsDataURL(file);
  });
}

/**
 * Pole s náhledy (`picture`) — nahrání fotky z disku, náhled a mazání.
 * Obrázek jde do R2, do záznamu se uloží jen jeho URL.
 */
export function PictureField({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const images = asImages(value);

  async function pick(file: File | null) {
    if (!file) return;
    setErr(null);
    if (!file.type.startsWith('image/')) {
      setErr('Vyber obrázek (JPG, PNG, WebP nebo GIF)');
      return;
    }
    if (file.size > MAX_BYTES) {
      setErr('Obrázek je větší než 3 MB');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await readDataUrl(file);
      const res = await apiFetch<{ url: string }>('/api/vyroba/obrazky', {
        method: 'POST',
        body: { data_url: dataUrl },
      });
      const next: Obrazek[] = [
        ...images.map((u) => ({ fileUrl: u, thumbnailUrl: u })),
        { fileUrl: res.url, thumbnailUrl: res.url },
      ];
      onChange(next);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.describe() : 'Nahrání obrázku selhalo');
    } finally {
      setBusy(false);
      // Ať jde stejný soubor vybrat znovu (input jinak změnu nehlásí).
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function remove(url: string) {
    const rest = images.filter((u) => u !== url);
    // Prázdné pole místo null — Tabidoo i naše čtení ho zvládnou stejně.
    onChange(rest.map((u) => ({ fileUrl: u, thumbnailUrl: u })));
  }

  return (
    <div className="flex flex-wrap items-center gap-2 py-1">
      {images.map((u) => (
        <div key={u} className="relative">
          <img
            src={u}
            alt=""
            loading="lazy"
            className="h-24 w-24 object-contain rounded-lg border border-border bg-white"
          />
          <button
            type="button"
            onClick={() => remove(u)}
            title="Odebrat obrázek"
            className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-white border border-border text-text-muted hover:text-danger hover:border-danger shadow-sm cursor-pointer leading-none"
          >
            ×
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="h-24 w-24 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-text-muted hover:border-primary hover:text-primary transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="text-2xl leading-none">+</span>
        <span className="text-xs font-medium">{busy ? 'Nahrávám…' : 'Nahrát fotku'}</span>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={(e) => pick(e.target.files?.[0] ?? null)}
        className="hidden"
      />

      {err && <p className="basis-full text-xs font-medium text-danger">{err}</p>}
    </div>
  );
}
